'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Star, Heart, MessageCircle, MapPin, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Props {
  userId: string;
}

interface ReviewItem {
  id: string;
  cafeId: string;
  cafeName: string;
  rating: number;
  text: string;
  photos: string[];
  likes: number;
  comments: number;
  timeAgo: string;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m lalu`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}j lalu`;
  return `${Math.floor(hrs / 24)}h lalu`;
}

export default function MyReviewsGrid({ userId }: Props) {
  const supabase = createClient();
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select(`id, coffee_shop_id, rating, content, photo_urls, like_count, comment_count, created_at, coffee_shops(name)`)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.log('My reviews error:', error.message);
        setLoading(false);
        return;
      }

      setReviews(
        (data || []).map((r: any) => ({
          id: r.id,
          cafeId: r.coffee_shop_id,
          cafeName: r.coffee_shops?.name || 'Cafe',
          rating: r.rating,
          text: r.content,
          photos: r.photo_urls || [],
          likes: r.like_count || 0,
          comments: r.comment_count || 0,
          timeAgo: timeAgo(r.created_at),
        }))
      );
      setLoading(false);
    };

    fetchReviews();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 size={24} className="animate-spin text-accent" />
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-5xl mb-4">✍️</div>
        <p className="text-base font-700 text-primary mb-2">Belum ada ulasan</p>
        <p className="text-sm text-muted-foreground mb-5">Kamu belum pernah nulis ulasan. Yuk share pengalaman nongkrong kamu!</p>
        <Link href="/" className="btn-primary inline-flex">Mulai Explore</Link>
      </div>
    );
  }

  return (
    <div className="masonry-2 lg:masonry-3">
      {reviews.map((review) => (
        <div key={`mrev-${review.id}`} className="masonry-item review-card">
          {review.photos?.[0] && (
            <div className="relative w-full aspect-square overflow-hidden">
              <AppImage
                src={review.photos[0]}
                alt={`Foto ulasan ${review.cafeName}`}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 50vw, 33vw"
              />
            </div>
          )}
          <div className="p-3">
            <Link
              href={`/coffeshop-detail-page?id=${review.cafeId}`}
              className="flex items-center gap-1 text-xs font-600 text-accent hover:underline mb-1"
            >
              <MapPin size={10} />
              {review.cafeName}
            </Link>
            <div className="flex items-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={`rs-${review.id}-${s}`}
                  size={11}
                  className={s <= Math.round(review.rating) ? 'rating-star fill-current' : 'text-muted'}
                />
              ))}
              <span className="text-xs text-muted-foreground ml-1 font-tabular">{review.rating?.toFixed(1)}</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">{review.text}</p>
            <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border">
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Heart size={11} /> {review.likes}
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <MessageCircle size={11} /> {review.comments}
              </span>
              <span className="text-xs text-muted-foreground ml-auto">{review.timeAgo}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}